import React from "react";
import { Grid, TextField, Autocomplete, Box } from "@mui/material";
import {
  WhatsApp,
  Telegram,
  Instagram,
  Facebook,
  Twitter,
  LinkedIn,
  YouTube,
  Reddit,
  Pinterest,
  CameraAlt,
  MusicNote,
  Language,
  SportsEsports,
  Code,
  Article,
  Palette,
  Brush,
  ChatBubble,
  Mic,
  Photo,
  Work,
  ViewQuilt,
} from "@mui/icons-material";

// Social media platform options with icons
const socialMediaPlatforms = [
  { label: "WhatsApp", value: "WhatsApp", icon: WhatsApp, color: "#25D366" },
  { label: "Telegram", value: "Telegram", icon: Telegram, color: "#0088cc" },
  { label: "Instagram", value: "Instagram", icon: Instagram, color: "#E4405F" },
  { label: "Facebook", value: "Facebook", icon: Facebook, color: "#1877F2" },
  { label: "Twitter (X)", value: "Twitter", icon: Twitter, color: "#000000" },
  { label: "LinkedIn", value: "LinkedIn", icon: LinkedIn, color: "#0A66C2" },
  { label: "YouTube", value: "YouTube", icon: YouTube, color: "#FF0000" },
  { label: "TikTok", value: "TikTok", icon: MusicNote, color: "#000000" },
  { label: "Snapchat", value: "Snapchat", icon: CameraAlt, color: "#FFFC00" },
  { label: "Discord", value: "Discord", icon: ChatBubble, color: "#5865F2" },
  { label: "Twitch", value: "Twitch", icon: SportsEsports, color: "#9146FF" },
  { label: "GitHub", value: "GitHub", icon: Code, color: "#181717" },
  { label: "Reddit", value: "Reddit", icon: Reddit, color: "#FF4500" },
  { label: "Pinterest", value: "Pinterest", icon: Pinterest, color: "#E60023" },
  { label: "Medium", value: "Medium", icon: Article, color: "#000000" },
  { label: "Spotify", value: "Spotify", icon: Mic, color: "#1DB954" },
  { label: "Flickr", value: "Flickr", icon: Photo, color: "#0063DC" },
  { label: "Stack Overflow", value: "StackOverflow", icon: Code, color: "#F58025" },
  { label: "Quora", value: "Quora", icon: ChatBubble, color: "#B92B27" },
  { label: "Mastodon", value: "Mastodon", icon: Language, color: "#6364FF" },
  { label: "Threads", value: "Threads", icon: ChatBubble, color: "#000000" },
  { label: "Vimeo", value: "Vimeo", icon: YouTube, color: "#1AB7EA" },
  { label: "Slack", value: "Slack", icon: ChatBubble, color: "#4A154B" },
  { label: "Unsplash", value: "Unsplash", icon: Photo, color: "#000000" },
  { label: "Codepen", value: "Codepen", icon: Code, color: "#000000" },
  { label: "GitLab", value: "GitLab", icon: Code, color: "#FC6D26" },
  { label: "Figma", value: "Figma", icon: ViewQuilt, color: "#F24E1E" },
  { label: "Other", value: "Other", icon: Language, color: "#666666" },
];

/**
 * Get platform details by value
 */
const getPlatformDetails = (value) => {
  return socialMediaPlatforms.find((p) => p.value === value) || socialMediaPlatforms[socialMediaPlatforms.length - 1];
};

/**
 * Get placeholder text based on platform
 */
const getPlaceholder = (type) => {
  const placeholders = {
    WhatsApp: "Enter WhatsApp number or link",
    Telegram: "Enter Telegram username or link",
    Instagram: "Enter Instagram username or profile URL",
    Facebook: "Enter Facebook profile URL",
    Twitter: "Enter Twitter/X username or profile URL",
    LinkedIn: "Enter LinkedIn profile URL",
    YouTube: "Enter YouTube channel URL",
    TikTok: "Enter TikTok username or profile URL",
    Snapchat: "Enter Snapchat username",
    Discord: "Enter Discord username or server link",
    Twitch: "Enter Twitch username or channel URL",
    GitHub: "Enter GitHub username or profile URL",
    Reddit: "Enter Reddit username or profile URL",
    Pinterest: "Enter Pinterest profile URL",
    Medium: "Enter Medium username or profile URL",
    Spotify: "Enter Spotify artist or playlist URL",
    Flickr: "Enter Flickr username or profile URL",
    StackOverflow: "Enter Stack Overflow profile URL",
    Quora: "Enter Quora profile URL",
    Mastodon: "Enter Mastodon handle or profile URL",
    Threads: "Enter Threads username or profile URL",
    Vimeo: "Enter Vimeo channel URL",
    Slack: "Enter Slack workspace URL",
    Unsplash: "Enter Unsplash profile URL",
    Codepen: "Enter Codepen username or profile URL",
    GitLab: "Enter GitLab username or profile URL",
    Figma: "Enter Figma profile URL",
    Other: "Enter link URL",
  };
  return placeholders[type] || "Enter link";
};

const SocialLinksFields = ({
  formData,
  handleChange,
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
}) => {
  const typeError = fieldErrors[`socialtype${section}`] || "";
  const linkError = fieldErrors[`sociallink${section}`] || "";

  const selectedType = formData[`socialtype${section}`] || "";
  const selectedPlatform = getPlatformDetails(selectedType);
  const IconComponent = selectedPlatform.icon;

  return (
    <Grid container spacing={3}>
      {/* Platform Type - Left Side */}
      <Grid item xs={12} md={5}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Platform <span className="text-red-500">*</span>
        </label>
        <Autocomplete
          options={socialMediaPlatforms}
          getOptionLabel={(option) => option.label || option}
          value={socialMediaPlatforms.find((p) => p.value === selectedType) || null}
          onChange={(e, newValue) => {
            handleChange({
              target: {
                name: `socialtype${section}`,
                value: newValue ? newValue.value : "",
              },
            });
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "8px 16px !important",
              }}
            >
              <option.icon sx={{ fontSize: 24, color: option.color }} />
              <span>{option.label}</span>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select platform"
              sx={textInputStyle}
              error={!!typeError}
              helperText={typeError}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedType && (
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1, mr: -0.5 }}>
                    <IconComponent sx={{ fontSize: 24, color: selectedPlatform.color }} />
                  </Box>
                ),
              }}
            />
          )}
        />
      </Grid>

      {/* Link - Right Side */}
      <Grid item xs={12} md={7}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Link / Username <span className="text-red-500">*</span>
        </label>
        <TextField
          fullWidth
          name={`sociallink${section}`}
          value={formData[`sociallink${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder={getPlaceholder(selectedType)}
          sx={textInputStyle}
          error={!!linkError}
          helperText={linkError || `Add your ${selectedType || "social media"} link or username`}
        />
      </Grid>
    </Grid>
  );
};

export default SocialLinksFields;
